import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Show, SignInButton, UserButton } from "@clerk/react";
import { Crown, Menu, ShoppingBag, X } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { useCart } from "@/context/CartContext";
import { StatusBadge } from "@/components/club/StatusBadge";
import { num } from "@/lib/api";
import { ChatWidget } from "@/components/ChatWidget";
import { CookieConsent, openCookieSettings } from "@/components/CookieConsent";
import { COMPANY, LEGAL_LINKS } from "@/pages/Legal";

const FOOTER = [
  ["Esplora", [["/", "Confronta prezzi"], ["/marketplace", "Marketplace"], ["/club", "Status, punti e premi"], ["/diventa-premium", "Diventa Gold o Diamond"], ["/blog", "Blog"]]],
  ["Azienda", [["/chi-siamo", "Chi siamo"], ["/partner", "Diventa partner"], ["/account", "La mia area"]]],
  ["Note legali", LEGAL_LINKS.map(([s, l]) => [`/legal/${s}`, l])],
];

const NavItem = ({ to, children, testid, onClick }) => (
  <NavLink
    to={to}
    end={to === "/"}
    onClick={onClick}
    data-testid={testid}
    className={({ isActive }) =>
      `whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`
    }
  >
    {children}
  </NavLink>
);

function Links({ onClick }) {
  const { me } = useMe();
  return (
    <>
      <NavItem to="/" testid="nav-compare" onClick={onClick}>Confronta</NavItem>
      <NavItem to="/marketplace" testid="nav-marketplace" onClick={onClick}>Marketplace</NavItem>
      <NavItem to="/club" testid="nav-club" onClick={onClick}>Club</NavItem>
      {me?.status !== "diamond" && <NavItem to="/diventa-premium" testid="nav-premium" onClick={onClick}>{me?.status === "gold" ? "Diamond" : "Premium"}</NavItem>}
      {me && <NavItem to="/account" testid="nav-account" onClick={onClick}>La mia area</NavItem>}
      {me && <NavItem to="/partner" testid="nav-partner" onClick={onClick}>Partner</NavItem>}
      {me?.role === "admin" && <NavItem to="/admin" testid="nav-admin" onClick={onClick}>Admin</NavItem>}
    </>
  );
}

export default function Layout() {
  const { me } = useMe();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative z-10 flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#0B0C10]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-amber-300 to-amber-600 text-[#0B0C10]">
              <Crown className="h-5 w-5" />
            </span>
            <span className="whitespace-nowrap font-club text-[11px] font-bold text-white sm:text-base">CLUB <span className="text-amber-400">PREZZI</span></span>
          </Link>
          <nav className="ml-6 hidden items-center gap-1 xl:flex"><Links /></nav>
          <div className="ml-auto flex items-center gap-1.5 sm:gap-3">
            {me && (
              <Link to="/account" data-testid="header-points" className="hidden items-center gap-2 whitespace-nowrap rounded-full border border-white/10 px-3 py-1.5 sm:flex">
                <StatusBadge status={me.status} small />
                <span className="font-display text-sm font-semibold text-amber-300">{num(me.balance)} pt</span>
              </Link>
            )}
            <Link to="/carrello" data-testid="header-cart" className="relative rounded-full p-2 text-slate-300 hover:text-white">
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-black">{count}</span>
              )}
            </Link>
            <Show when="signed-out">
              <SignInButton>
                <button data-testid="header-signin" className="rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-amber-400">Accedi</button>
              </SignInButton>
            </Show>
            <Show when="signed-in"><UserButton /></Show>
            <button className="rounded-full p-2 text-slate-300 xl:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="flex flex-col gap-1 border-t border-white/5 px-4 py-3 xl:hidden">
            {me && (
              <div data-testid="mobile-points" className="mb-2 flex items-center gap-2 px-4">
                <StatusBadge status={me.status} small />
                <span className="font-display text-sm font-semibold text-amber-300">{num(me.balance)} pt</span>
              </div>
            )}
            <Links onClick={() => setOpen(false)} />
          </nav>
        )}
      </header>
      <main className="flex-1 pb-6 sm:pb-0"><Outlet /></main>
      <footer className="border-t border-white/5 pb-24 pt-14 text-sm text-slate-400" data-testid="site-footer">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
          <div>
            <p className="font-club text-sm font-bold text-white">CLUB <span className="text-amber-400">PREZZI</span></p>
            <p className="mt-3">Confronta i prezzi, guadagna punti, paga meno.</p>
            <p className="mt-3 text-xs text-slate-500">{COMPANY.name} · {COMPANY.address} · P.IVA {COMPANY.vat}</p>
          </div>
          {FOOTER.map(([title, links]) => (
            <div key={title}>
              <p className="font-semibold uppercase tracking-widest text-xs text-slate-500">{title}</p>
              <ul className="mt-3 space-y-2">
                {links.map(([to, label]) => <li key={to}><Link to={to} data-testid={`footer-link-${to.replace(/\//g, "-").slice(1) || "home"}`} className="hover:text-white">{label}</Link></li>)}
                {title === "Note legali" && <li><button data-testid="footer-cookie-settings" onClick={openCookieSettings} className="hover:text-white">Preferenze cookie</button></li>}
              </ul>
            </div>
          ))}
        </div>
        <div className="mx-auto mt-10 flex max-w-7xl flex-col gap-2 border-t border-white/5 px-4 pt-6 text-xs text-slate-500 sm:flex-row sm:justify-between sm:px-6">
          <span>© {new Date().getFullYear()} Club Prezzi · {COMPANY.name} · {COMPANY.email} · WhatsApp {COMPANY.whatsapp}</span>
          <span>Prezzi e spedizioni dei negozi esterni sono stime trovate sul web: verifica sempre sul sito del negozio.</span>
        </div>
      </footer>
      <ChatWidget />
      <CookieConsent />
    </div>
  );
}
